import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonDatetime,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonChip,
  IonList
} from '@ionic/react';
import {
  downloadOutline,
  calendarOutline,
  statsChartOutline,
  trendingUpOutline,
  cashOutline,
  ticketOutline,
  pieChartOutline,
  barChartOutline
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { reportService, saleService, ticketService } from '../services/api';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay } from 'date-fns';
import CenteredLoader from '../components/CenteredLoader';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState<'sales' | 'inventory' | 'profit'>('sales');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('month');
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [reportData, setReportData] = useState<any>(null);

  useEffect(() => {
    updateDateRange();
  }, [dateRange]);

  useEffect(() => {
    generateReport();
  }, [reportType, startDate, endDate]);

  const updateDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case 'today':
        setStartDate(startOfDay(now));
        setEndDate(endOfDay(now));
        break;
      case 'week':
        setStartDate(startOfWeek(now));
        setEndDate(endOfWeek(now));
        break;
      case 'month':
        setStartDate(startOfMonth(now));
        setEndDate(endOfMonth(now));
        break;
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      let data;
      console.log(`Generating ${reportType} report...`);
      switch (reportType) {
        case 'sales':
          data = await reportService.generateSalesReport({ startDate, endDate });
          console.log('Sales report data:', data);
          break;
        case 'inventory':
          data = await reportService.generateInventoryReport();
          console.log('Inventory report data:', data);
          break;
        case 'profit':
          data = await reportService.generateProfitReport({ startDate, endDate });
          console.log('Profit report data:', data);
          break;
      }
      setReportData(data);
    } catch (error) {
      console.error('Failed to generate report', error);
      // Set reportData to null on error so mock data is used as fallback
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'excel' | 'pdf') => {
    if (!reportData) return;
    
    try {
      const blob = await reportService.exportReport(reportData.id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${reportType}-${Date.now()}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report', error);
    }
  };

  const getMockSalesData = () => ({
    summary: {
      totalSales: 156,
      totalRevenue: 45600,
      averageTicketPrice: 292,
      topSellingEvent: 'Taylor Swift - Eras Tour'
    },
    salesByDay: [
      { date: 'Mon', sales: 12, revenue: 3600 },
      { date: 'Tue', sales: 19, revenue: 5700 },
      { date: 'Wed', sales: 15, revenue: 4500 },
      { date: 'Thu', sales: 25, revenue: 7500 },
      { date: 'Fri', sales: 32, revenue: 9600 },
      { date: 'Sat', sales: 28, revenue: 8400 },
      { date: 'Sun', sales: 25, revenue: 7500 }
    ],
    salesByVenue: [
      { name: 'Madison Square Garden', value: 45 },
      { name: 'Barclays Center', value: 32 },
      { name: 'MetLife Stadium', value: 28 },
      { name: 'Yankee Stadium', value: 25 },
      { name: 'Other', value: 26 }
    ],
    topEvents: [
      { event: 'Taylor Swift - Eras Tour', tickets: 45, revenue: 13500 },
      { event: 'Coldplay - Music of Spheres', tickets: 38, revenue: 11400 },
      { event: 'Ed Sheeran - Mathematics Tour', tickets: 32, revenue: 9600 },
      { event: 'Bruno Mars - 24K Magic', tickets: 28, revenue: 8400 },
      { event: 'The Weeknd - After Hours', tickets: 13, revenue: 3900 }
    ]
  });

  const getMockInventoryData = () => ({
    summary: {
      totalTickets: 342,
      availableTickets: 186,
      soldTickets: 156,
      lowStockEvents: 5
    },
    ticketsByStatus: [
      { name: 'Available', value: 186 },
      { name: 'Sold', value: 156 }
    ],
    ticketsByVenue: [
      { venue: 'Madison Square Garden', available: 45, sold: 32 },
      { venue: 'Barclays Center', available: 38, sold: 28 },
      { venue: 'MetLife Stadium', available: 52, sold: 41 },
      { venue: 'Yankee Stadium', available: 31, sold: 35 },
      { venue: 'Other', available: 20, sold: 20 }
    ]
  });

  const getMockProfitData = () => ({
    summary: {
      totalRevenue: 45600,
      totalCost: 32400,
      netProfit: 13200,
      profitMargin: 28.9
    },
    profitByMonth: [
      { month: 'Jan', revenue: 5600, cost: 4200, profit: 1400 },
      { month: 'Feb', revenue: 7200, cost: 5100, profit: 2100 },
      { month: 'Mar', revenue: 8900, cost: 6300, profit: 2600 },
      { month: 'Apr', revenue: 9500, cost: 6800, profit: 2700 },
      { month: 'May', revenue: 14400, cost: 10000, profit: 4400 }
    ],
    profitByEvent: [
      { name: 'Taylor Swift', value: 4500 },
      { name: 'Coldplay', value: 3200 },
      { name: 'Ed Sheeran', value: 2800 },
      { name: 'Bruno Mars', value: 1700 },
      { name: 'Other', value: 1000 }
    ]
  });

  const renderSalesReport = () => {
    // Use real data if available, otherwise fall back to mock data
    const mockData = getMockSalesData();
    let data = mockData;
    let hasRealData = false;
    
    if (reportData && reportData.summary && reportData.summary.totalSales !== undefined) {
      console.log('Using real sales report data:', reportData);
      hasRealData = true;
      
      // Transform backend data to match frontend expectations
      data = {
        summary: {
          totalSales: reportData.summary.totalSales || 0,
          totalRevenue: reportData.summary.totalRevenue || 0,
          averageTicketPrice: Math.round(reportData.summary.averageTicketPrice || 0),
          topSellingEvent: reportData.detailedSales?.length > 0 ? 
            reportData.detailedSales[0].eventId?.name || reportData.detailedSales[0].eventName || 'N/A' : 'N/A'
        },
        salesByDay: reportData.salesByDate?.length > 0 ? reportData.salesByDate.map((item: any) => ({
          date: new Date(item.date).toLocaleDateString('en', { weekday: 'short' }),
          sales: item.count,
          revenue: item.revenue
        })) : [],
        salesByVenue: reportData.salesByVenue?.length > 0 ? reportData.salesByVenue.map((item: any) => ({
          name: item.venue || item.name || 'Unknown Venue',
          value: item.count || item.value || 0
        })) : [],
        topEvents: reportData.detailedSales?.length > 0 ? reportData.detailedSales.slice(0, 5).map((sale: any) => ({
          event: sale.eventId?.name || sale.eventName || 'Unknown Event',
          tickets: sale.quantity || 1,
          revenue: sale.totalAmount || 0
        })) : []
      };
      
      // If we have no sales data, show empty state instead of mock data
      if (data.summary.totalSales === 0) {
        data.salesByDay = [];
        data.salesByVenue = [];
        data.topEvents = [];
      }
    } else {
      console.log('Using mock sales data - no real data available');
    }
    
    return (
      <>
        <IonRow className="gap-y-4">
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={statsChartOutline} className="text-5xl mb-4 text-primary" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {data.summary.totalSales}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('reports.totalSales')}
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={cashOutline} className="text-5xl mb-4 text-primary" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  ${data.summary.totalRevenue.toLocaleString()}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('reports.totalRevenue')}
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={ticketOutline} className="text-5xl mb-4 text-primary" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  ${data.summary.averageTicketPrice}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('reports.averageTicketPrice')}
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={trendingUpOutline} className="text-5xl mb-4 text-primary" />
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {data.summary.topSellingEvent}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Top Event
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow className="mt-4">
          <IonCol size="12" sizeLg="8">
            <IonCard>
              <IonCardHeader className="border-b border-gray-200 dark:border-gray-700">
                <IonCardTitle className="text-lg font-semibold">
                  {t('reports.salesByMonth')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="p-4">
                {hasRealData && data.salesByDay.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
                    <IonIcon icon={barChartOutline} className="text-6xl mb-4" />
                    <p>No sales data available for this period</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.salesByDay}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="sales" fill="#8884d8" />
                      <Bar dataKey="revenue" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>

          <IonCol size="12" sizeLg="4">
            <IonCard>
              <IonCardHeader className="border-b border-gray-200 dark:border-gray-700">
                <IonCardTitle className="text-lg font-semibold">
                  {t('reports.salesByVenue')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="p-4">
                {hasRealData && data.salesByVenue.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
                    <IonIcon icon={pieChartOutline} className="text-6xl mb-4" />
                    <p>No venue data available</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={data.salesByVenue}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => entry.name}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {data.salesByVenue.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow className="mt-4">
          <IonCol size="12">
            <IonCard>
              <IonCardHeader className="border-b border-gray-200 dark:border-gray-700">
                <IonCardTitle className="text-lg font-semibold">
                  {t('reports.topSellingEvents')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="p-0">
                {hasRealData && data.topEvents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[200px] text-gray-500">
                    <IonIcon icon={ticketOutline} className="text-6xl mb-4" />
                    <p>No events data available</p>
                  </div>
                ) : (
                  <IonList className="p-0">
                    {data.topEvents.map((event, index) => (
                      <IonItem key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <IonLabel className="py-3">
                          <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                            {event.event}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {event.tickets} tickets sold
                          </p>
                        </IonLabel>
                        <h3 slot="end" className="text-xl font-bold text-gray-800 dark:text-gray-100">
                          ${event.revenue.toLocaleString()}
                        </h3>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </>
    );
  };

  const renderInventoryReport = () => {
    // Use real data if available, otherwise fall back to mock data
    const mockData = getMockInventoryData();
    let data = mockData;
    let hasRealData = false;
    
    if (reportData && reportData.summary && reportData.summary.totalTickets !== undefined) {
      console.log('Using real inventory report data:', reportData);
      hasRealData = true;
      
      // Transform backend data to match frontend expectations
      data = {
        summary: {
          totalTickets: reportData.summary.totalTickets || 0,
          availableTickets: reportData.summary.availableTickets || 0,
          soldTickets: reportData.summary.soldTickets || 0,
          lowStockEvents: reportData.summary.lowStockEvents || 0
        },
        ticketsByStatus: [
          { name: 'Available', value: reportData.summary.availableTickets || 0 },
          { name: 'Sold', value: reportData.summary.soldTickets || 0 }
        ],
        ticketsByVenue: reportData.ticketsByVenue?.length > 0 ? reportData.ticketsByVenue.map((item: any) => ({
          venue: item.venue || 'Unknown Venue',
          available: item.available || 0,
          sold: item.sold || 0
        })) : []
      };
    } else {
      console.log('Using mock inventory data - no real data available');
    }
    
    return (
      <>
        <IonRow className="gap-y-4">
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={ticketOutline} className="text-5xl mb-4 text-primary" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {data.summary.totalTickets}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Tickets
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={ticketOutline} className="text-5xl mb-4 text-green-600" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {data.summary.availableTickets}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Available
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={ticketOutline} className="text-5xl mb-4 text-red-600" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {data.summary.soldTickets}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Sold
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={ticketOutline} className="text-5xl mb-4 text-yellow-600" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {data.summary.lowStockEvents}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Low Stock Events
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow className="mt-4">
          <IonCol size="12" sizeLg="6">
            <IonCard>
              <IonCardHeader className="border-b border-gray-200 dark:border-gray-700">
                <IonCardTitle className="text-lg font-semibold">
                  Ticket Status Distribution
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.ticketsByStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.ticketsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </IonCardContent>
            </IonCard>
          </IonCol>

          <IonCol size="12" sizeLg="6">
            <IonCard>
              <IonCardHeader className="border-b border-gray-200 dark:border-gray-700">
                <IonCardTitle className="text-lg font-semibold">
                  Tickets by Venue
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.ticketsByVenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="venue" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="available" fill="#82ca9d" />
                    <Bar dataKey="sold" fill="#ff7979" />
                  </BarChart>
                </ResponsiveContainer>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </>
    );
  };

  const renderProfitReport = () => {
    // Use real data if available, otherwise fall back to mock data
    const mockData = getMockProfitData();
    let data = mockData;
    let hasRealData = false;
    
    if (reportData && reportData.summary && reportData.summary.totalRevenue !== undefined) {
      console.log('Using real profit report data:', reportData);
      hasRealData = true;
      
      // Transform backend data to match frontend expectations
      data = {
        summary: {
          totalRevenue: reportData.summary.totalRevenue || 0,
          totalCost: reportData.summary.totalCost || 0,
          netProfit: reportData.summary.netProfit || 0,
          profitMargin: reportData.summary.profitMargin || 0
        },
        profitByMonth: reportData.profitByMonth?.length > 0 ? reportData.profitByMonth.map((item: any) => ({
          month: new Date(item.month + '-01').toLocaleDateString('en', { month: 'short' }),
          revenue: item.revenue,
          cost: item.cost,
          profit: item.profit
        })) : [],
        profitByEvent: reportData.detailedSales?.length > 0 ? reportData.detailedSales.slice(0, 5).map((sale: any) => ({
          name: sale.eventId?.name || sale.eventName || 'Unknown Event',
          value: sale.netProfit || 0
        })) : []
      };
    } else {
      console.log('Using mock profit data - no real data available');
    }
    
    return (
      <>
        <IonRow className="gap-y-4">
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={cashOutline} className="text-5xl mb-4 text-green-600" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  ${data.summary.totalRevenue.toLocaleString()}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('reports.totalRevenue')}
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={cashOutline} className="text-5xl mb-4 text-red-600" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  ${data.summary.totalCost.toLocaleString()}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Cost
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={trendingUpOutline} className="text-5xl mb-4 text-blue-600" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  ${data.summary.netProfit.toLocaleString()}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('reports.totalProfit')}
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol size="12" sizeMd="3">
            <IonCard className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <IonCardContent className="text-center p-6">
                <IonIcon icon={pieChartOutline} className="text-5xl mb-4 text-primary" />
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {data.summary.profitMargin.toFixed(1)}%
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('reports.profitMargins')}
                </p>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow className="mt-4">
          <IonCol size="12" sizeLg="8">
            <IonCard>
              <IonCardHeader className="border-b border-gray-200 dark:border-gray-700">
                <IonCardTitle className="text-lg font-semibold">
                  Profit Trend
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.profitByMonth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#82ca9d" />
                    <Line type="monotone" dataKey="cost" stroke="#ff7979" />
                    <Line type="monotone" dataKey="profit" stroke="#8884d8" />
                  </LineChart>
                </ResponsiveContainer>
              </IonCardContent>
            </IonCard>
          </IonCol>

          <IonCol size="12" sizeLg="4">
            <IonCard>
              <IonCardHeader className="border-b border-gray-200 dark:border-gray-700">
                <IonCardTitle className="text-lg font-semibold">
                  Profit by Event
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.profitByEvent}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.name}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.profitByEvent.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </>
    );
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton menu="main-menu" />
          </IonButtons>
          <IonTitle>{t('reports.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => handleExport('pdf')}>
              <IonIcon icon={downloadOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonGrid className="p-4 md:p-6">
          <IonRow className="mb-4">
            <IonCol size="12">
              {/* Date Range Controls */}
              <IonCard className="mb-4">
                <IonCardContent>
                  <IonGrid>
                    <IonRow className="ion-align-items-center">
                      <IonCol size="12" sizeMd="4">
                        <IonItem lines="none">
                          <IonLabel position="stacked">Date Range</IonLabel>
                          <IonSelect value={dateRange} onIonChange={(e) => setDateRange(e.detail.value)}>
                            <IonSelectOption value="today">Today</IonSelectOption>
                            <IonSelectOption value="week">This Week</IonSelectOption>
                            <IonSelectOption value="month">This Month</IonSelectOption>
                            <IonSelectOption value="custom">Custom Range</IonSelectOption>
                          </IonSelect>
                        </IonItem>
                      </IonCol>
                      {dateRange === 'custom' && (
                        <>
                          <IonCol size="12" sizeMd="4">
                            <IonItem lines="none">
                              <IonLabel position="stacked">Start Date</IonLabel>
                              <IonDatetime 
                                value={startDate.toISOString()} 
                                onIonChange={(e) => setStartDate(new Date(e.detail.value as string))}
                                presentation="date"
                              />
                            </IonItem>
                          </IonCol>
                          <IonCol size="12" sizeMd="4">
                            <IonItem lines="none">
                              <IonLabel position="stacked">End Date</IonLabel>
                              <IonDatetime 
                                value={endDate.toISOString()} 
                                onIonChange={(e) => setEndDate(new Date(e.detail.value as string))}
                                presentation="date"
                              />
                            </IonItem>
                          </IonCol>
                        </>
                      )}
                    </IonRow>
                  </IonGrid>
                </IonCardContent>
              </IonCard>
              
              {/* Data source indicator */}
              {reportData && reportData.summary && (
                (reportType === 'sales' && reportData.summary.totalSales !== undefined) ||
                (reportType === 'inventory' && reportData.summary.totalTickets !== undefined) ||
                (reportType === 'profit' && reportData.summary.totalRevenue !== undefined)
              ) ? (
                <IonChip color="success" className="mt-2">
                  <IonLabel>Real Data</IonLabel>
                </IonChip>
              ) : (
                <IonChip color="warning" className="mt-2">
                  <IonLabel>Sample Data (No Data Available)</IonLabel>
                </IonChip>
              )}
            </IonCol>
          </IonRow>
          
          <IonRow>
            <IonCol size="12">
              <IonSegment value={reportType} onIonChange={(e) => setReportType(e.detail.value as any)}>
                <IonSegmentButton value="sales">
                  <IonLabel>{t('reports.salesReport')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="inventory">
                  <IonLabel>{t('reports.inventoryReport')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="profit">
                  <IonLabel>{t('reports.profitReport')}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCol>
          </IonRow>

          <IonRow className="mt-4">
            <IonCol size="12" sizeMd="6">
              <IonSelect
                value={dateRange}
                onIonChange={(e) => setDateRange(e.detail.value)}
                interface="popover"
                className="w-full"
              >
                <IonSelectOption value="today">{t('reports.today')}</IonSelectOption>
                <IonSelectOption value="week">{t('reports.thisWeek')}</IonSelectOption>
                <IonSelectOption value="month">{t('reports.thisMonth')}</IonSelectOption>
                <IonSelectOption value="custom">{t('reports.custom')}</IonSelectOption>
              </IonSelect>
            </IonCol>
            
            {dateRange === 'custom' && (
              <>
                <IonCol size="12" sizeMd="3">
                  <IonDatetime
                    value={startDate.toISOString()}
                    onIonChange={(e) => {
                      const value = e.detail.value;
                      if (typeof value === 'string') {
                        setStartDate(new Date(value));
                      }
                    }}
                    presentation="date"
                  />
                </IonCol>
                <IonCol size="12" sizeMd="3">
                  <IonDatetime
                    value={endDate.toISOString()}
                    onIonChange={(e) => {
                      const value = e.detail.value;
                      if (typeof value === 'string') {
                        setEndDate(new Date(value));
                      }
                    }}
                    presentation="date"
                  />
                </IonCol>
              </>
            )}
          </IonRow>

          <IonRow>
            <IonCol size="12">
              <div className="flex gap-2 flex-wrap my-4">
                <IonChip 
                  onClick={() => handleExport('csv')} 
                  className="cursor-pointer transition-transform hover:-translate-y-1"
                >
                  <IonIcon icon={downloadOutline} />
                  <IonLabel>{t('reports.exportCSV')}</IonLabel>
                </IonChip>
                <IonChip 
                  onClick={() => handleExport('excel')} 
                  className="cursor-pointer transition-transform hover:-translate-y-1"
                >
                  <IonIcon icon={downloadOutline} />
                  <IonLabel>{t('reports.exportExcel')}</IonLabel>
                </IonChip>
                <IonChip 
                  onClick={() => handleExport('pdf')} 
                  className="cursor-pointer transition-transform hover:-translate-y-1"
                >
                  <IonIcon icon={downloadOutline} />
                  <IonLabel>{t('reports.exportPDF')}</IonLabel>
                </IonChip>
              </div>
            </IonCol>
          </IonRow>

          {loading ? (
            <IonRow>
              <IonCol className="h-96 flex items-center justify-center">
                <CenteredLoader message={t('reports.generatingReport') || 'Generating report...'} />
              </IonCol>
            </IonRow>
          ) : (
            <>
              {reportType === 'sales' && renderSalesReport()}
              {reportType === 'inventory' && renderInventoryReport()}
              {reportType === 'profit' && renderProfitReport()}
            </>
          )}
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default Reports;